'use client';

import {
  Button,
  ConfirmationDialog,
  HeadingItem,
  IconButton,
  MetaDefault,
  Spinner,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Cancel01Icon, Radar01Icon, WorkHistoryIcon } from '@terra/ui-icons';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { getMcpCredentialAuthTypeLabel } from '@terra/ui-ai-applications';
import { useApplicationCredentialsList } from '../../hooks/api/useApplicationCredentialsList';
import { useAiApplication } from '../../hooks/api/useAiApplication';
import {
  useAiDiscoveryExecutions,
  useAiDiscoveryRunControls,
  useAiDiscoverySummary,
} from '../../hooks/api/useAiDiscovery';
import { useMcpServerIdentities } from '../../hooks/api/useMcpServerIdentities';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsMcpServerTarget } from '../../store/projectStore';
import { navigateToApplicationSettingsSubTab } from '../../utils/navigateToApplicationSettingsSubTab';
import { formatElapsedTimer } from '../crawler/crawlerHistory.utils';
import {
  buildAgentIdentityRows,
  buildMcpIdentityRows,
} from './ai-discovery-identity.util';
import { resolveIsAiDiscoveryRunning } from './ai-discovery-running.util';
import { AiDiscoveryIdentityRow } from './AiDiscoveryIdentityRow';
import { useAiDiscoveryCanRun } from './useAiDiscoveryCanRun';
import { useAiApplicationComponents } from '../../hooks/api/useAiApplicationComponents';
import { AiDiscoveryCoverage } from './AiDiscoveryCoverage';

type AiDiscoveryMonitorPanelProps = {
  applicationId: string;
  onOpenHistory: () => void;
  onClose?: () => void;
  hasHistoryUnseen?: boolean;
};

export function AiDiscoveryMonitorPanel({
  applicationId,
  onOpenHistory,
  onClose,
  hasHistoryUnseen,
}: AiDiscoveryMonitorPanelProps) {
  const t = useTranslations('discovery.aiApplication');
  const tCommon = useTranslations('common');
  const tMcpAuth = useTranslations('settings.mcpServerForm');
  const { canManageApplications } = usePermissions();
  const isMcpServer = useIsMcpServerTarget();
  const [isStopConfirmOpen, setIsStopConfirmOpen] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const { aiApplication, isLoading: isAppLoading } = useAiApplication({
    aiApplicationId: applicationId,
  });
  const mcpServerId = aiApplication?.mcpServerId ?? '';
  const { data: credentialsData, isLoading: isCredentialsLoading } =
    useApplicationCredentialsList({
      applicationId,
      enabled: !isMcpServer,
    });
  const { identities, isLoading: isIdentitiesLoading } = useMcpServerIdentities(
    {
      serverId: isMcpServer ? mcpServerId : '',
    },
  );
  const executionsQuery = useAiDiscoveryExecutions({
    applicationId,
    enabled: !!applicationId,
  });
  const discoverySummaryQuery = useAiDiscoverySummary({
    aiApplicationId: applicationId,
    enabled: !!applicationId,
  });
  const { runDiscovery, stopDiscovery, isRunPending, isStopPending } =
    useAiDiscoveryRunControls({
      aiApplicationId: applicationId,
    });

  const componentsQuery = useAiApplicationComponents({
    aiApplicationId: applicationId,
  });
  const discoverySummary = discoverySummaryQuery.discoverySummary;
  const isDiscoveryRunning = resolveIsAiDiscoveryRunning({
    executions: executionsQuery.executions,
    discoverySummary,
  });
  const { canRun, isDraft, needsMcpAuth } = useAiDiscoveryCanRun({
    applicationId,
    isRunPending,
  });

  const identityRows = useMemo(() => {
    if (isMcpServer) {
      return buildMcpIdentityRows({
        identities,
        defaultLabel: t('defaultIdentity'),
        formatAuthType: (type) =>
          getMcpCredentialAuthTypeLabel({
            credentialType: type,
            translate: tMcpAuth,
          }),
      });
    }

    return buildAgentIdentityRows({
      credentials: credentialsData?.credentials ?? [],
      anonymousLabel: t('anonymous'),
    });
  }, [credentialsData?.credentials, identities, isMcpServer, t, tMcpAuth]);

  useEffect(() => {
    if (!isDiscoveryRunning) {
      return;
    }

    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [isDiscoveryRunning]);

  const handleManageSettings = useCallback(() => {
    navigateToApplicationSettingsSubTab(
      isMcpServer ? 'mcp-connection' : 'credentials',
    );
    onClose?.();
  }, [isMcpServer, onClose]);

  const liveLabel = useMemo(() => {
    if (discoverySummary?.awaitingCodeDiscovery) {
      return t('awaitingCode');
    }

    if (isMcpServer) {
      return t('liveMcp', {
        count: discoverySummary?.pendingProbeCount ?? 0,
      });
    }

    return t('liveAgent');
  }, [
    discoverySummary?.awaitingCodeDiscovery,
    discoverySummary?.pendingProbeCount,
    isMcpServer,
    t,
  ]);

  const startedAt = discoverySummary?.discoveryStartedAt;
  const isLoading =
    isAppLoading ||
    (isMcpServer ? isIdentitiesLoading : isCredentialsLoading) ||
    (discoverySummaryQuery.isLoading && discoverySummary == null);

  if (isLoading) {
    return (
      <Box p="4" display="flex" justifyContent="center">
        <Spinner size="sm" />
      </Box>
    );
  }

  const historyLabel = t('history');
  const closeLabel = tCommon('close');
  const helperText = isDraft
    ? t('finishSetup')
    : needsMcpAuth
      ? t('authorizeFirst')
      : undefined;

  return (
    <>
      <VStack gap="0" w="full" alignItems="stretch">
        <HStack
          justifyContent="space-between"
          w="full"
          px="3"
          py="3"
          gap="2"
          borderBottomWidth="1px"
          borderColor="border.muted"
        >
          <HStack gap="2" alignItems="center">
            <Radar01Icon size={18} />
            <HeadingItem>{t('title')}</HeadingItem>
          </HStack>

          <HStack gap="2">
            {canManageApplications ? (
              isDiscoveryRunning ? (
                <Button
                  size="xs"
                  variant="outline"
                  colorPalette="red"
                  loading={isStopPending}
                  disabled={isStopPending}
                  onClick={() => setIsStopConfirmOpen(true)}
                >
                  {t('stop')}
                </Button>
              ) : (
                <Button
                  size="xs"
                  variant="outline"
                  loading={isRunPending}
                  disabled={!canRun}
                  onClick={runDiscovery}
                >
                  {t('run')}
                </Button>
              )
            ) : null}
            <Tooltip title={historyLabel} positioning={{ strategy: 'fixed' }}>
              <Box position="relative">
                <IconButton
                  variant="ghost"
                  size="xs"
                  onClick={onOpenHistory}
                  aria-label={t('history')}
                >
                  <WorkHistoryIcon size={16} />
                </IconButton>
                {hasHistoryUnseen ? (
                  <Box
                    position="absolute"
                    top="1px"
                    right="1px"
                    w="2"
                    h="2"
                    borderRadius="full"
                    bg="bg.status.resolved"
                  />
                ) : null}
              </Box>
            </Tooltip>
            {onClose ? (
              <Tooltip title={closeLabel} positioning={{ strategy: 'fixed' }}>
                <IconButton
                  variant="ghost"
                  size="xs"
                  onClick={onClose}
                  aria-label={tCommon('close')}
                >
                  <Cancel01Icon size={16} />
                </IconButton>
              </Tooltip>
            ) : null}
          </HStack>
        </HStack>

        {helperText ? (
          <Box px="3" py="2" borderBottomWidth="1px" borderColor="border.muted">
            <MetaDefault color="fg.muted">{helperText}</MetaDefault>
          </Box>
        ) : null}

        {componentsQuery.components.length > 0 ? (
          <Box px="3" py="2" borderBottomWidth="1px" borderColor="border.muted">
            <AiDiscoveryCoverage components={componentsQuery.components} />
          </Box>
        ) : null}

        {isDiscoveryRunning ? (
          <HStack
            w="full"
            px="3"
            py="2"
            gap="2"
            justifyContent="space-between"
            bg="bg.subtle"
            borderBottomWidth="1px"
            borderColor="border.muted"
          >
            <HStack gap="2" alignItems="center">
              <Spinner size="xs" />
              <MetaDefault>{liveLabel}</MetaDefault>
            </HStack>
            {startedAt ? (
              <MetaDefault color="fg.muted">
                {formatElapsedTimer({
                  startedAt,
                  now: new Date(nowMs),
                })}
              </MetaDefault>
            ) : null}
          </HStack>
        ) : null}

        {identityRows.length === 1 && identityRows[0]?.id === 'anonymous' ? (
          <Box px="3" py="3">
            <MetaDefault color="fg.muted">{t('noExtraUsers')}</MetaDefault>
          </Box>
        ) : (
          <VStack gap="0" w="full" alignItems="stretch">
            {identityRows.map((row, index) => (
              <Fragment key={row.id}>
                <AiDiscoveryIdentityRow
                  row={row}
                  hideDivider={index === identityRows.length - 1}
                />
              </Fragment>
            ))}
          </VStack>
        )}

        <HStack
          px="3"
          py="2"
          gap="2"
          justifyContent="space-between"
          borderTopWidth="1px"
          borderColor="border.muted"
        >
          <Button size="xs" variant="ghost" onClick={handleManageSettings}>
            <MetaDefault color="fg.muted">{t('manageInSettings')}</MetaDefault>
          </Button>
          {identityRows.length > 1 ? (
            <MetaDefault color="fg.muted">{t('usesAllUsers')}</MetaDefault>
          ) : null}
        </HStack>
      </VStack>

      <ConfirmationDialog
        isOpen={isStopConfirmOpen}
        onOpenChange={({ open }) => {
          if (!open) {
            setIsStopConfirmOpen(false);
          }
        }}
        onConfirm={() => {
          stopDiscovery({
            onStopped: () => {
              setIsStopConfirmOpen(false);
            },
          });
        }}
        loading={isStopPending}
        variant="destructive"
        title={t('stopConfirmTitle')}
        description={t('stopConfirmDescription')}
        confirmText={t('stop')}
        cancelText={tCommon('cancel')}
      />
    </>
  );
}
